/**
 * Common types shared across multiple modules
 * Extracted to reduce code duplication
 */

/**
 * Generic paginated response wrapper
 */
export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

/**
 * Generic list response with total and pagination info
 * Used by PO, SO, SQ, Inbound, etc.
 */
export interface ListResponse<T> {
  total: number;
  limit: number;
  offset: number;
  items: T[];
}

/**
 * Base line item structure shared by PO, SO, SQ
 */
export interface BaseLineItem {
  line_no: number;
  product_name: string;
  specification: string;
  quantity: number;
  unit_price: number;
  amount: number;
  currency: string;
  remarks?: string;
}

/**
 * Base filter for list queries
 */
export interface BaseFilter {
  limit?: number;
  offset?: number;
  start_date?: string;
  end_date?: string;
}

/**
 * Priority levels used across multiple modules (Kanban, MES, etc.)
 */
export type Priority = 'low' | 'normal' | 'medium' | 'high' | 'critical';

/**
 * Priority colors for consistent styling
 */
export const PRIORITY_COLORS: Record<Priority, string> = {
  low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  normal: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

/**
 * Priority labels (Chinese)
 */
export const PRIORITY_LABELS: Record<Priority, string> = {
  low: '低',
  normal: '普通',
  medium: '中',
  high: '高',
  critical: '緊急',
};

/**
 * Common currency options
 */
export const CURRENCY_OPTIONS = [
  { value: 'TWD', label: 'TWD (新台幣)', symbol: 'NT$' },
  { value: 'USD', label: 'USD (美元)', symbol: '$' },
  { value: 'CNY', label: 'CNY (人民幣)', symbol: '¥' },
  { value: 'EUR', label: 'EUR (歐元)', symbol: '€' },
  { value: 'JPY', label: 'JPY (日圓)', symbol: '¥' },
] as const;

/**
 * Currency symbols map
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  TWD: 'NT$',
  USD: '$',
  EUR: '€',
  JPY: '¥',
  CNY: '¥',
};

/**
 * Default values
 */
export const DEFAULT_CURRENCY = 'TWD';
export const DEFAULT_TAX_RATE = 0.05;
export const DEFAULT_PAGE_SIZE = 20;

/**
 * Format currency amount with symbol
 */
export function formatCurrency(amount: number, currency: string = 'TWD'): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Calculate line item amount
 */
export function calculateItemAmount(quantity: number, unitPrice: number): number {
  return quantity * unitPrice;
}

/**
 * Calculate totals with optional tax
 */
export function calculateTotals(
  items: Array<{ quantity: number; unit_price: number }>,
  taxRate: number = DEFAULT_TAX_RATE
): { subtotal: number; taxAmount: number; grandTotal: number } {
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const taxAmount = subtotal * taxRate;
  const grandTotal = subtotal + taxAmount;
  return { subtotal, taxAmount, grandTotal };
}

/**
 * Format date string to localized format
 */
export function formatDate(dateStr: string, locale: string = 'zh-TW'): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Format date range
 */
export function formatDateRange(from: string, until: string): string {
  return `${formatDate(from)} ~ ${formatDate(until)}`;
}
